import { LinkIcon } from '@/assets/icons'
import Modal from '@components/Modal'
import { copyTextToClipboard } from '@lib/clipboard'
import { publicTripSectionUrl } from '@lib/publicLinks'
import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import styled, { css } from 'styled-components'
import { black, border2, darkOrange, gold, success, surface2, textMuted } from '@/theme/colors'
import { fontMono } from '@/theme/fonts'
import { radii, iconSizes } from '@/theme/tokens'
import { fontSizes, leading } from '@/theme/typography'
import { Button } from '@components/Button'

interface ShareItineraryModalProps {
	isOpen: boolean
	onClose: () => void
	tripCode: string
	isVisibleToPublic: boolean
}

const Description = styled.p`
	margin: 0 0 20px 0;
	font-size: ${fontSizes.base};
	color: ${textMuted};
	line-height: ${leading.body};
`

const LinkRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	background: ${surface2};
	border: 1px solid ${border2};
	border-radius: ${radii.small};
	padding: 10px 12px;
	margin-bottom: 20px;
`

const LinkText = styled.span`
	flex: 1;
	font-size: ${fontSizes.small};
	color: ${black};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-family: ${fontMono};
`

const CopyButton = styled(Button).attrs({ size: 'sm' as const })<{ $copied: boolean }>`
	flex-shrink: 0;

	${({ $copied }) =>
		$copied &&
		css`
			background: ${success};

			&:hover:not(:disabled) {
				background: ${success};
			}
		`}
`

const VisibilityBanner = styled.div<{ $isPublic: boolean }>`
	display: flex;
	align-items: flex-start;
	gap: 10px;
	padding: 12px 16px;
	border-radius: ${radii.small};
	margin-bottom: 20px;
	font-size: ${fontSizes.small};
	line-height: ${leading.body};
	background: ${props => (props.$isPublic ? `${success}1f` : `${gold}33`)};
	color: ${props => (props.$isPublic ? success : darkOrange)};
	border: 1px solid ${props => (props.$isPublic ? `${success}52` : `${gold}80`)};
`

const StatusDot = styled.span<{ $isPublic: boolean }>`
	width: 8px;
	height: 8px;
	min-width: 8px;
	border-radius: 50%;
	margin-top: 5px;
	background: ${props => (props.$isPublic ? success : gold)};
`

const Hint = styled.p`
	margin: 0;
	font-size: ${fontSizes.caption};
	color: ${textMuted};
	line-height: ${leading.body};
`

const ShareItineraryModal: React.FC<ShareItineraryModalProps> = ({
	isOpen,
	onClose,
	tripCode,
	isVisibleToPublic,
}) => {
	const [copied, setCopied] = useState(false)

	const publicUrl = publicTripSectionUrl(tripCode, 'itinerary')

	const handleCopy = async () => {
		try {
			await copyTextToClipboard(publicUrl)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch (error) {
			toast.error('Could not copy the link. Please copy it manually.')
			console.error('Error copying public link:', error)
		}
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Share Itinerary" closeOnBackdropClick size="medium">
			<Description>
				Anyone with this link can view your itinerary — no Orbit account required.
			</Description>

			<LinkRow>
				<LinkIcon sx={{ fontSize: iconSizes.sm }} htmlColor={textMuted} />
				<LinkText>{publicUrl}</LinkText>
				<CopyButton $copied={copied} onClick={handleCopy}>
					{copied ? 'Copied!' : 'Copy'}
				</CopyButton>
			</LinkRow>

			<VisibilityBanner $isPublic={isVisibleToPublic}>
				<StatusDot $isPublic={isVisibleToPublic} />
				{isVisibleToPublic
					? 'Your itinerary is public. Anyone with the link above can view it.'
					: 'Your itinerary is currently private — the link above will not work until you make it public.'}
			</VisibilityBanner>

			<Hint>
				You can toggle public visibility for this itinerary in your Trip Settings.
			</Hint>
		</Modal>
	)
}

export default ShareItineraryModal
